/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { MouseEvent, useCallback, useRef } from "react";
import { BEM } from "@adapptio/lib-components";
import {
	FadeInOut,
	FC_ANCHOR,
	FC_DIRECTION,
	FC_POSITION,
	FloatingContainer,
	FloatingContainerContent,
	FloatingContainerHeader
} from "../../core";
import { FC_SIZE } from "../../core/FloatingContainer/FloatingContainer";
import { isFunction } from "@adapptio/lib-shared";
import { useBackdropClick, useForkRef } from "../../../hooks";

export enum FIELD_FLOATING_PANEL_STYLE {
	STICKY = "sticky",
	ARROW = "arrow"
}

export enum FIELD_FLOATING_PANEL_SCHEMA {
	/** Primary panel color */
	PRIMARY = "primary",
	/** Dropdown panel color */
	DROPDOWN = "dropdown"
}

export interface IFieldFloatingPanelProps {
	/** Element to align panel to */
	for: React.MutableRefObject<HTMLElement>;
	/** If the panel is opened */
	isOpened: boolean;
	/** Where to place panel relative to target element */
	anchor?: FC_ANCHOR;
	/** Direction of panel relative to target element */
	direction?: FC_DIRECTION;
	/** How far to place element from target */
	offset?: number;
	/** Size of floating container. Might be height or width, depends on "direction" value. */
	size?: FC_SIZE;
	/** How to place panel (if stick to target or display arrow pointing to it) */
	style?: FIELD_FLOATING_PANEL_STYLE;
	/** Color schema */
	schema?: FIELD_FLOATING_PANEL_SCHEMA;
	/** Custom class name */
	className?: string;
	/** Called when panel is closed */
	onClose?: () => void;
	/** Text in header */
	title?: string;
	disableBackdrop?: boolean;
	children?: React.ReactNode;
	hideCloseButton?: boolean;
	titleSecondary?: React.ReactNode;
}

export const FieldFloatingPanel = React.forwardRef<HTMLDivElement, IFieldFloatingPanelProps>(
	(
		{
			for: target,
			isOpened,
			anchor,
			direction,
			offset,
			style,
			schema,
			className,
			onClose,
			size,
			children,
			title,
			disableBackdrop,
			hideCloseButton,
			titleSecondary
		},
		ref
	) => {
		const panelRef = useRef();

		const forkRef = useForkRef(panelRef, ref);

		const preventClick = useCallback(
			(ev: MouseEvent) => {
				if (isOpened) {
					ev.stopPropagation();
				}
			},
			[ isOpened ]
		);

		useBackdropClick(
			(event) => {
				if (!disableBackdrop && isOpened && isFunction(onClose)) {
					event.preventDefault();
					event.stopPropagation();
					onClose();
				}
			},
			[ panelRef, target ],
			isOpened && !disableBackdrop
		);

		return (
			<FadeInOut
				isVisible={isOpened}
				fadeOutDelay={250}
				render={(isVisible) => {
					const baseClass = BEM("edt-field-floating-panel", null, {
						"is-visible": isVisible,
						style: style || FIELD_FLOATING_PANEL_STYLE.ARROW,
						schema: schema || FIELD_FLOATING_PANEL_SCHEMA.PRIMARY
					});

					if (className) {
						baseClass.add(className);
					}

					return (
						<FloatingContainer
							ref={forkRef}
							parent={target}
							anchor={anchor || FC_ANCHOR.LEFT_CENTER}
							direction={direction || FC_DIRECTION.VR}
							size={size}
							position={FC_POSITION.AUTO}
							className={baseClass.toClassName()}
							addArrow={true}
							offset={offset !== undefined ? offset : 0}
							safePadding={10}
							updateInterval={200}
							onClose={onClose}
						>
							{title && (
								<FloatingContainerHeader
									onClose={onClose}
									hideCloseButton={hideCloseButton}
									titleSecondary={titleSecondary}
								>
									{title}
								</FloatingContainerHeader>
							)}
							<FloatingContainerContent
								className={baseClass.element("content").toClassName()}
								onClick={preventClick}
							>
								{children}
							</FloatingContainerContent>
						</FloatingContainer>
					);
				}}
			/>
		);
	}
);

FieldFloatingPanel.defaultProps = {
	size: FC_SIZE.AUTO
};
